import { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { INPUT_DOCUMENT_SAVE_DEBOUNCE_MS } from 'constants/appConstants';
import { INPUTS_QUERY_KEY } from 'constants/queryKeys';
import { IInput } from 'interfaces/IInput';
import { IInputTask } from 'interfaces/IInputTask';
import InputService from 'services/InputService';
import { toast } from 'utils/toast';
import { getApiErrorMessage } from 'utils/UtilityMethods';
import { TASK_DELETE_ERROR_MESSAGE, TASK_SAVE_ERROR_MESSAGE } from 'constants/messageConstants';

interface PendingTaskEntry {
  operation: 'upsert' | 'delete';
  task: IInputTask | null;
  timeout: ReturnType<typeof setTimeout>;
  // Set to true the moment the debounce timer fires and runEntry begins.
  // Used in scheduleEntry to distinguish "timer not yet fired" from
  // "request already in-flight" when deciding whether to cancel the entry.
  hasFired: boolean;
}

interface UsePendingTaskSaveArgs {
  inputId: string;
  activeOrganisationId: string | null;
}

export const usePendingTaskSave = ({
  inputId,
  activeOrganisationId,
}: UsePendingTaskSaveArgs) => {
  const queryClient = useQueryClient();
  const pendingMapRef = useRef<Map<string, PendingTaskEntry>>(new Map());
  // Tracks task IDs that were created locally in this session and have not yet
  // been acknowledged by the server. Only tasks scheduled via
  // schedulePendingNewTaskSave are added here. Used to distinguish a
  // "just created, never persisted" task (safe to cancel both sides) from an
  // "existing server task with pending edits" (must still send a DELETE).
  const localOnlyTaskIdsRef = useRef<Set<string>>(new Set());
  const [hasPendingTaskChanges, setHasPendingTaskChanges] = useState(false);
  const [pendingTaskCount, setPendingTaskCount] = useState(0);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);

  // Called after a successful API response to reconcile the cache with the
  // server's authoritative state. Cache only — BrowserStorage/draft are owned
  // by the draft task mutators (addDraftTask/updateDraftTask/removeDraftTask
  // in useInputDocumentDraft) and must not be overwritten here, since the
  // user may have made further local edits while this request was in flight.
  const applyTaskServerUpdate = useCallback(
    (updatedInput: IInput) => {
      if (!activeOrganisationId) return;

      queryClient.setQueryData<IInput[]>(
        [INPUTS_QUERY_KEY, activeOrganisationId],
        (current) => {
          if (!current) return current;
          return current.map((item) =>
            item.id === updatedInput.id ? updatedInput : item
          );
        }
      );
    },
    [activeOrganisationId, queryClient]
  );

  const finishEntry = useCallback((taskId: string) => {
    pendingMapRef.current.delete(taskId);
    const remaining = pendingMapRef.current.size;
    setPendingTaskCount(remaining);
    if (remaining === 0) {
      setHasPendingTaskChanges(false);
    }
  }, []);

  const runEntry = useCallback(
    async (taskId: string, entry: PendingTaskEntry) => {
      const clientNow = new Date().toISOString();

      try {
        const response =
          entry.operation === 'upsert' && entry.task
            ? await InputService.UpsertTask(inputId, entry.task, clientNow)
            : await InputService.DeleteTask(inputId, taskId, clientNow);

        const updatedInput = response?.data?.[0];
        if (updatedInput) {
          applyTaskServerUpdate(updatedInput);
        }
        // Task is now confirmed on the server — it is no longer "local only".
        if (entry.operation === 'upsert') {
          localOnlyTaskIdsRef.current.delete(taskId);
        }
        const parsed = updatedInput?.dateModified
          ? Date.parse(updatedInput.dateModified)
          : NaN;
        setLastSavedAt(Number.isNaN(parsed) ? Date.now() : parsed);
      } catch (error) {
        const message = getApiErrorMessage(
          error,
          entry.operation === 'upsert'
            ? TASK_SAVE_ERROR_MESSAGE
            : TASK_DELETE_ERROR_MESSAGE
        );
        toast.error(message);
      } finally {
        // Guard against wiping a newer entry: if scheduleEntry replaced this
        // entry in the map while our request was in-flight (race condition),
        // leave the newer entry intact so its timer can still fire.
        if (pendingMapRef.current.get(taskId) === entry) {
          finishEntry(taskId);
        }
      }
    },
    [applyTaskServerUpdate, finishEntry, inputId]
  );

  const scheduleEntry = useCallback(
    (
      taskId: string,
      operation: PendingTaskEntry['operation'],
      task: IInputTask | null
    ) => {
      const existing = pendingMapRef.current.get(taskId);

      if (existing) {
        clearTimeout(existing.timeout);

        // If the task was created locally (never confirmed by the server) AND
        // its upsert timer hasn't fired yet, deleting it is a no-op — cancel
        // both sides without any API call. We must NOT do this for tasks that
        // already exist on the server (even if they have a pending upsert edit),
        // because those still need a DELETE to be sent.
        if (
          localOnlyTaskIdsRef.current.has(taskId) &&
          !existing.hasFired &&
          existing.operation === 'upsert' &&
          operation === 'delete'
        ) {
          localOnlyTaskIdsRef.current.delete(taskId);
          pendingMapRef.current.delete(taskId);
          const remaining = pendingMapRef.current.size;
          setPendingTaskCount(remaining);
          if (remaining === 0) setHasPendingTaskChanges(false);
          return;
        }
      }

      const timeout = setTimeout(() => {
        const entry = pendingMapRef.current.get(taskId);
        if (entry) {
          entry.hasFired = true;
          runEntry(taskId, entry);
        }
      }, INPUT_DOCUMENT_SAVE_DEBOUNCE_MS);

      pendingMapRef.current.set(taskId, {
        operation,
        task,
        timeout,
        hasFired: false,
      });
      setPendingTaskCount(pendingMapRef.current.size);
      setHasPendingTaskChanges(true);
    },
    [runEntry]
  );

  const schedulePendingTaskSave = useCallback(
    (task: IInputTask) => {
      scheduleEntry(task.taskId, 'upsert', task);
    },
    [scheduleEntry]
  );

  // Like schedulePendingTaskSave, but marks the task as "local only" — it was
  // just created in this session and has never been confirmed by the server.
  // Call this instead of schedulePendingTaskSave when creating a brand new task
  // so that an immediate delete (before the debounce fires) can be short-circuited
  // without sending any API calls.
  const schedulePendingNewTaskSave = useCallback(
    (task: IInputTask) => {
      localOnlyTaskIdsRef.current.add(task.taskId);
      scheduleEntry(task.taskId, 'upsert', task);
    },
    [scheduleEntry]
  );

  const schedulePendingTaskDelete = useCallback(
    (taskId: string) => {
      scheduleEntry(taskId, 'delete', null);
    },
    [scheduleEntry]
  );

  // Immediately fires all pending task saves/deletes, bypassing the remaining
  // debounce window. Called when the user explicitly clicks Save.
  const flushPendingTaskSaves = useCallback(() => {
    const snapshot = Array.from(pendingMapRef.current.entries());
    snapshot.forEach(([, entry]) => {
      clearTimeout(entry.timeout);
      entry.hasFired = true;
    });
    snapshot.forEach(([taskId, entry]) => {
      runEntry(taskId, entry);
    });
  }, [runEntry]);

  // Cancels every pending task timer without firing the API calls. Called
  // when the user discards local changes, so no in-flight task save can
  // land after the discard.
  const cancelAllPendingTaskSaves = useCallback(() => {
    pendingMapRef.current.forEach((entry) => clearTimeout(entry.timeout));
    pendingMapRef.current.clear();
    localOnlyTaskIdsRef.current.clear();
    setPendingTaskCount(0);
    setHasPendingTaskChanges(false);
  }, []);

  useEffect(() => {
    setLastSavedAt(null);
    localOnlyTaskIdsRef.current.clear();
    const pendingMap = pendingMapRef.current;
    return () => {
      pendingMap.forEach((entry) => clearTimeout(entry.timeout));
      pendingMap.clear();
      localOnlyTaskIdsRef.current.clear();
    };
  }, [inputId]);

  return {
    hasPendingTaskChanges,
    pendingTaskCount,
    lastSavedAt,
    schedulePendingTaskSave,
    schedulePendingNewTaskSave,
    schedulePendingTaskDelete,
    flushPendingTaskSaves,
    cancelAllPendingTaskSaves,
  };
};

export default usePendingTaskSave;
